// =============================================================================
// ui/tabs/CommissionTable.tsx — the firm's commission rates, inside Edit Account.
// -----------------------------------------------------------------------------
// The rate a firm charges per contract per side. Until this landed there was no
// way to enter one in this client at all — so any trade whose broker export
// carried no fee was counted at zero cost, and every net P&L that touched it
// read better than the trader's real result.
//
// ── 🚨 IT IS THE ONLY CONTROL ON THIS SCREEN THAT REACHES PAST ONE ACCOUNT ──
//
// A schedule belongs to a FIRM. Changing Apex's ES rate moves every ES trade on
// every Apex account, live or blown or archived. The heading says so in those
// words, because someone editing "Apex 50k #2" has no other reason to expect it.
//
// ── 🚨 IT SAVES ON ITS OWN BUTTON, NOT WITH THE ACCOUNT ──
//
// Tempting to fold it into the modal's Save, and wrong: the account write and
// the schedule write have different blast radii, and one button doing both means
// a trader correcting a nickname cannot avoid also committing a half-typed rate.
// Two writes, two buttons, each saying what it touches.
//
// ── ⚠ WHAT IS DELIBERATELY NOT HERE: "Apply to existing trades" ──
//
// The old client needs that button because it stores the fee on the row. This
// client DERIVES it on read (`selectors/derive.ts`), so a saved rate is already
// in every figure on every tab the moment the page re-reads — there is nothing
// to backfill. A button that rewrote hundreds of stored rows to match what is
// already on screen would be the largest write in the app in exchange for
// nothing visible. The note below says that rather than leaving a gap.
// =============================================================================

import { useMemo, useState } from 'react';
import { useSupabase } from '../state/supabase';
import { useContracts } from '../state/contracts';
import { useCommissions } from '../state/commissions';
import { saveCommissionSchedule } from '../../io/sync/commissionSchedules';
import {
  addRate, blockingRateErrors, draftFromSchedule, draftIsDirty, normalizeFirm,
  ratesFromDraft, removeRate, roundTurn, setRate, validateDraft,
  type CommissionDraft,
} from '../../selectors/commissionEditor';
import { moneyPlain } from '../format';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface CommissionTableProps {
  /** The firm as currently typed in the form ABOVE — not the saved one. */
  firm: string;
}

export function CommissionTable({ firm }: CommissionTableProps) {
  const { sb, userId } = useSupabase();
  const { contracts } = useContracts();
  const { phase, schedules, reload } = useCommissions();

  const firmKey = normalizeFirm(firm);
  const stored = schedules && firmKey ? schedules[firmKey] : undefined;

  /**
   * ⚠ RE-SEEDED WHEN THE FIRM CHANGES, via the key rather than an effect.
   * Typing a different firm into the box above is a different schedule, and a
   * draft carried across would offer to write one firm's rates under another
   * firm's name. `key` on the inner component is the React-shaped way to say
   * "this is a different thing now" — an effect would run a render late, and
   * that render is one where the rows and the heading disagree.
   */
  if (!firmKey) {
    return (
      <div className="ae-note ae-comm-empty">
        <b>Commission rates are stored per firm.</b> Fill in <b>Firm</b> above and
        they appear here — the same rates then apply to every account you have at
        that firm.
      </div>
    );
  }

  /**
   * 🚨 THE GATE IS "HAVE WE EVER READ", NOT "ARE WE READING".
   *
   * Saving reloads the provider, which flips the phase back to `loading` — and
   * gating on the phase unmounts the editor mid-save. Everything it was holding
   * goes with it: the "Saved 3 rates for Apex" confirmation is destroyed the
   * instant it appears, and the table visibly snaps back to the old figures
   * until the read lands. Pressing Save appears to do nothing.
   *
   * `schedules` keeps its previous value throughout a reload, so this shows the
   * spinner only on the FIRST read, when there genuinely is nothing to edit.
   * Found by a test, not by review — and it would have been invisible against a
   * fast local database.
   */
  if (phase === 'loading' && !schedules) {
    return <p className="muted ae-comm-empty">Reading your commission rates…</p>;
  }
  if (phase === 'failed' || !schedules) {
    // ⚠ NOT AN EDITOR OVER AN UNKNOWN STATE. Rendering an empty table here would
    // invite a save that wipes rates that are actually on file.
    return (
      <div className="ae-error ae-comm-empty" role="alert">
        Couldn’t read your commission rates, so they can’t be edited safely right
        now — saving from here could overwrite rates that are already stored.
      </div>
    );
  }

  return (
    <CommissionEditor
      key={firmKey}
      firm={firm}
      stored={stored}
      contracts={(contracts ?? []).map((c) => ({ symbol: c.symbol, name: c.name }))}
      onSaved={reload}
      save={(rates) => saveCommissionSchedule(sb, userId, firm, rates)}
    />
  );
}

interface Pick { symbol: string; name: string }

function CommissionEditor(
  { firm, stored, contracts, save, onSaved }: {
    firm: string;
    stored: { firm: string; rates: Readonly<Record<string, number>> } | undefined;
    contracts: readonly Pick[];
    save: (rates: Record<string, number>) => Promise<{ ok: boolean; error: string | null }>;
    onSaved: () => void;
  },
) {
  const [draft, setDraft] = useState<CommissionDraft>(
    () => draftFromSchedule(firm, stored),
  );
  const [adding, setAdding] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const errors = useMemo(() => validateDraft(draft), [draft]);
  const blocking = useMemo(() => blockingRateErrors(errors), [errors]);
  const dirty = draftIsDirty(draft, stored);
  const errorFor = (sym: string) => errors.find((e) => e.symbol === sym)?.message ?? null;

  /** Instruments not already in the table, so the picker never offers a no-op. */
  const available = useMemo(
    () => contracts.filter((c) => !draft.rows.some((r) => r.symbol === c.symbol)),
    [contracts, draft.rows],
  );

  const onSave = async () => {
    setBusy(true);
    setError(null);
    setSaved(null);
    const rates = ratesFromDraft(draft);
    const res = await save(rates).catch((e: unknown) => ({
      // ⚠ The lesson D140 paid for: a thrown fetch REJECTS rather than
      // returning, and without this the block sits on "Saving…" for ever.
      ok: false, error: reportFailure('save commission rates', e),
    }));
    setBusy(false);
    if (!res.ok) { setError(res.error ?? 'That did not save.'); return; }
    const n = Object.keys(rates).length;
    setSaved(
      n
        ? `Saved ${n} rate${n === 1 ? '' : 's'} for ${firm}. Every ${firm} trade’s cost is recalculated from these.`
        : `Cleared every rate for ${firm}. Its trades now carry no automatic commission at all.`,
    );
    onSaved();
  };

  return (
    <div className="ae-comm">
      <div className="muted ae-comm-help">
        Dollars per contract <b>per side</b> — the round turn is twice this.
        {' '}🚨 These apply to <b>every {firm} account you have</b>, not just this
        one, and to trades already logged: the cost is worked out from this table
        each time a figure is shown. An instrument that is not listed carries{' '}
        <b>no commission at all</b>.
      </div>

      <table className="table ae-comm-table">
        <thead>
          <tr>
            <th>Instrument</th>
            <th>Per side, $/contract</th>
            <th>Round turn</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {draft.rows.length === 0 ? (
            <tr>
              <td colSpan={4} className="muted">
                No instruments yet — nothing you trade at {firm} is being charged a
                commission. Add one below.
              </td>
            </tr>
          ) : draft.rows.map((r) => {
            const msg = errorFor(r.symbol);
            const n = Number(r.input);
            return (
              <tr key={r.symbol}>
                <td className="ae-comm-sym">{r.symbol}</td>
                <td>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    aria-label={`${r.symbol} per-side rate`}
                    value={r.input}
                    onChange={(e) => setDraft((d) => setRate(d, r.symbol, e.target.value))}
                  />
                  {msg ? <span className="ae-msg">{msg}</span> : null}
                </td>
                {/* ⚠ THE ROUND TURN IS SHOWN BECAUSE IT IS THE FIGURE FIRMS
                    QUOTE. A round-turn number typed into a per-side box doubles
                    every fee the app will ever charge, and this is the line that
                    makes that visible while it is still being typed. */}
                <td className="muted ae-comm-rt">
                  {Number.isFinite(n) ? `${moneyPlain(roundTurn(n))}/ct` : '—'}
                </td>
                <td>
                  <button
                    type="button"
                    className="btn danger ae-comm-del"
                    aria-label={`Remove ${r.symbol}`}
                    onClick={() => setDraft((d) => removeRate(d, r.symbol))}
                  >×</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="ae-comm-add">
        <select
          aria-label="Instrument to add"
          value={adding || available[0]?.symbol || ''}
          onChange={(e) => setAdding(e.target.value)}
          disabled={!available.length}
        >
          {available.map((c) => (
            <option key={c.symbol} value={c.symbol}>{c.symbol} — {c.name}</option>
          ))}
        </select>
        <button
          type="button"
          className="btn"
          disabled={!available.length}
          onClick={() => {
            const sym = adding || available[0]?.symbol;
            if (sym) { setDraft((d) => addRate(d, sym)); setAdding(''); }
          }}
        >+ Add instrument</button>
        <span className="grow" />
        {/* ⚠ ITS OWN SAVE. See the header — this write and the account write have
            different reach, and one button for both would make correcting a
            nickname commit a half-typed rate too. */}
        <button
          type="button"
          className={`btn${dirty ? ' primary' : ''}`}
          disabled={busy || !dirty || blocking.length > 0}
          onClick={() => void onSave()}
        >
          {busy ? 'Saving…' : `Save ${firm} rates`}
        </button>
      </div>

      {blocking.length ? (
        <div className="ae-msg ae-comm-msg" role="alert">
          Fix the {blocking.length === 1 ? 'rate' : 'rates'} above before saving.
        </div>
      ) : null}
      {error ? <div className="ae-error ae-comm-msg" role="alert">{error}</div> : null}
      {saved ? <div className="green ae-comm-msg" role="status">{saved}</div> : null}

      {/* 🚨 WHY THERE IS NO "APPLY TO EXISTING TRADES" BUTTON, said plainly.
          Someone arriving from the current client will look for it, and its
          absence reads as a missing feature rather than as a thing that is no
          longer needed. */}
      <p className="muted ae-comm-foot">
        There is no “apply to existing trades” step here. The current client
        stores each trade’s fee on the trade, so it needs one; this one works the
        cost out from the table above every time it shows you a figure, so saving
        a rate has already changed every trade it applies to.
      </p>
    </div>
  );
}
